import { AfterViewInit, Component, ContentChildren, ElementRef, Input, QueryList, ViewChildren } from '@angular/core';
import { StatusProgressItemComponent } from '../status-progress-item/status-progress-item.component';

@Component({
    selector: 'sc-status-progress',
    templateUrl: './status-progress.component.html',
    styleUrls: ['./status-progress.component.scss'],
    standalone: false
})
export class StatusProgressComponent implements AfterViewInit {
  @Input('selected-status')
  selectedStatus: any;

  @ContentChildren(StatusProgressItemComponent)
  items?: QueryList<StatusProgressItemComponent>;

  @ViewChildren('labelRef')
  labelElements?: QueryList<ElementRef<HTMLElement>>;

  lastLabelHalfWidthPx = 0;

  get activeTrackWidth(): string {
    if (this.activeIndex < 0) {
      return '0%';
    }

    return this.getTrackPosition(this.activeIndex);
  }

  get trackLineWidth(): string {
    if (this.lastLabelHalfWidthPx <= 0) {
      return '100%';
    }

    return `calc(100% - ${this.lastLabelHalfWidthPx}px)`;
  }

  get projectedItems(): StatusProgressItemComponent[] {
    return this.items?.toArray() ?? [];
  }

  get activeIndex(): number {
    if (this.hasSelectedStatus()) {
      return this.projectedItems.findIndex(item => this.valuesMatch(item.value, this.selectedStatus));
    }

    return this.projectedItems.reduce((lastActiveIndex, item, index) => item.active ? index : lastActiveIndex, -1);
  }

  getStepPosition(index: number): string {
    if (this.isLast(index)) {
      return '100%';
    }

    return this.getTrackPosition(index);
  }

  isFirst(index: number): boolean {
    return index === 0;
  }

  isLast(index: number): boolean {
    return index === this.projectedItems.length - 1;
  }

  isActive(item: StatusProgressItemComponent): boolean {
    const index = this.projectedItems.indexOf(item);

    if (index < 0) {
      return false;
    }

    return index <= this.activeIndex;
  }

  isClickable(item: StatusProgressItemComponent): boolean {
    return item.clickable;
  }

  onItemClick(item: StatusProgressItemComponent) {
    if (!item.clickable) {
      return;
    }

    item.emitClick();
  }

  getDotLeft(index: number): string | null {
    if (!this.isLast(index) || this.lastLabelHalfWidthPx <= 0) {
      return null;
    }

    return `-${this.lastLabelHalfWidthPx}px`;
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => this.measureLabels());
    this.labelElements?.changes.subscribe(() => queueMicrotask(() => this.measureLabels()));
    this.items?.changes.subscribe(() => queueMicrotask(() => this.measureLabels()));
  }

  private getPositionPercent(index: number): number {
    if (this.projectedItems.length < 2) {
      return 0;
    }

    return (index / (this.projectedItems.length - 1)) * 100;
  }

  private getTrackPosition(index: number): string {
    const percent = this.getPositionPercent(index);

    if (this.lastLabelHalfWidthPx <= 0 || percent <= 0) {
      return `${percent}%`;
    }

    return `calc((100% - ${this.lastLabelHalfWidthPx}px) * ${percent / 100})`;
  }

  private valuesMatch(left: any, right: any): boolean {
    if (left === right) {
      return true;
    }

    return this.normalizeValue(left) === this.normalizeValue(right);
  }

  private hasSelectedStatus(): boolean {
    return this.selectedStatus !== null && this.selectedStatus !== undefined && this.normalizeValue(this.selectedStatus) !== '';
  }

  private normalizeValue(value: any): any {
    if (typeof value !== 'string') {
      return value;
    }

    const trimmed = value.trim();
    const hasSingleQuotes = trimmed.startsWith('\'') && trimmed.endsWith('\'');
    const hasDoubleQuotes = trimmed.startsWith('"') && trimmed.endsWith('"');

    if (hasSingleQuotes || hasDoubleQuotes) {
      return trimmed.slice(1, -1);
    }

    return trimmed;
  }

  private measureLabels() {
    const labelElements = this.labelElements?.toArray() ?? [];
    const lastLabel = labelElements.at(-1)?.nativeElement;
    this.lastLabelHalfWidthPx = lastLabel ? lastLabel.offsetWidth / 2 : 0;
  }
}
